/**
 * tizenAvplay — wrapper ISOLADO do `webapis.avplay(store)` (Tizen), fundação do backend
 * AVPlay (doc: efflux-server/docs/roadmaps/FEATURE-AVPLAY-TIZEN.md).
 *
 * Objetivo: expor o AVPlay num contrato pequeno e testável que MAPEIA nos handlers que o
 * `<video>` do Stream já usa (onLoadedData/onCanPlay/onPlaying/onTimeUpdate/onEnded/
 * onError), pra o backend encaixar sem reescrever a lógica do Stream.
 *
 * NÃO acopla no Stream nem no React — só fala com `webapis.avplay(store)`. Só deve ser
 * usado no caminho AVPlay (flag `VITE_AVPLAY_BACKEND` + platform tizen). Fora disso, `<video>`.
 *
 * === COSTURA "CLOSE-THE-PAST" (2 players, 1 decoder) — fix do leak nativo (validado spike) ===
 * A versão 1-player (stop→open reusando o MESMO player, SEM close) VAZAVA memória nativa do
 * decoder a cada troca → OOM/kill da OS em ~5h (crash silencioso do 1.0.254). Dar `close()` no
 * player ÚNICO daria tela preta (fecha o plano visível). Solução (ideia do Carlos, provada na
 * QB50B): **2 players alternando + `close()` SÓ no player do PASSADO**, e só DEPOIS do frame
 * novo já estar na tela. Nunca 2 decoders ao mesmo tempo (o antigo é congelado/parado ANTES
 * do novo preparar) → 1 decoder, gapless, e libera a memória nativa a cada troca.
 * Medido no spike: 12 trocas → 11 closes → 1 player vivo, quotaErr=0, memória NORMAL flat.
 *
 * StillMode na troca: `setVideoStillMode('true')` no player que SAI segura o último frame no
 * plano HW enquanto o player que ENTRA prepara → sem preto. Na Tizen o cover DOM não segura
 * frame de vídeo (WebCodecs/readback mortos) → o still-mode do plano HW é o único jeito.
 */
import { isChangeOn, noteChangeAction } from '~/services/diagnostics/changeSpy';
import { markBoot } from '~/utils/bootTiming';
import { isPlayableMediaUrl } from './playableUrl';

const USE_STILLMODE = true;

/**
 * Flag de experimento (2026-07-24): quando `true`, FORÇA o caminho de 1 PLAYER reusado
 * (still→stop→open→prepare→still→play na MESMA instância) mesmo com `avplaystore` (SSSP), em
 * vez do close-the-past de 2 players.
 *
 * DESLIGADA (Renato, v1.0.274) — DECISÃO: o **2-player (close-the-past) é a config mais
 * estável** e fica como default. Motivo (validado): tirar o 2-player só deixa 2 caminhos
 * PIORES — (a) 1-player reusado SEM close = **vazamento nativo → OOM/kill em ~5h** (foi o
 * crash da 1.0.254), ou (b) `<video>` HTML5 = estável mas **volta o PRETO na transição**. O
 * 2-player é 1 decoder alternado + still-mode cobrindo o gap = **gapless + sem leak** em HW de
 * 1 decoder (QB50B). O teste de single-player (v1.0.273) foi REFUTADO no consumer (crashou
 * igual) e não era necessário na SSSP (o revert do release-hold, 1.0.272, já a estabilizou
 * ~9,5h). NÃO religar sem novo motivo forte.
 */
export const FORCE_SINGLE_PLAYER = false;

/** Watchdog: se o 1º frame do player novo não subir, fecha o passado mesmo assim (ms). */
const CLOSE_PAST_FALLBACK_MS = 1200;

/**
 * C2 — recovery de `onresourceconflicted` (decoder reclamado pelo sistema/2º processo). Cap +
 * debounce + janela deslizante pra re-open in-place NÃO virar loop open→conflita→reopen que
 * acelera a morte de CMA. Portado da variante reuse (testado sem efeito colateral).
 */
const RESOURCE_CONFLICT_WINDOW_MS = 60_000;
const RESOURCE_CONFLICT_DEBOUNCE_MS = 1_000;
const RESOURCE_CONFLICT_MAX_RECOVERIES = 2;

/**
 * Fix A (2026-07-25): timeout do `prepareAsync`. Se NEM o success NEM o error callback vier
 * dentro disto, assumimos que o `prepareAsync` TRAVOU silencioso (a regressão conhecida do
 * 6.5 em `file://`) e caímos pro `prepare()` SÍNCRONO no mesmo player — pior caso = o
 * comportamento atual (síncrono, bloqueante), NUNCA tela preta. Generoso de propósito: um
 * `prepareAsync` LENTO (mas funcionando) NÃO bloqueia a main thread, então não há pressa em
 * cair pro síncrono — só queremos pegar o STALL de verdade (callback que nunca chega).
 */
const PREPARE_ASYNC_TIMEOUT_MS = 5000;

/** "Perto do fim": distância (ms) da duração pra armar o watchdog de fim de vídeo. */
const END_NEAR_MS = 400;
/** Se `onstreamcompleted` não vier em até isto após o near-end, FORÇA o fim (avança). */
const END_GRACE_MS = 2500;

/** Liga o log de diagnóstico da costura (bancada). */
const AVPLAY_LOG = true;
function log(msg: string): void {
  if (AVPLAY_LOG) {
    try {
      console.log('[AVPlay2P] ' + msg);
    } catch {
      /* noop */
    }
  }
}

/**
 * Breadcrumb da ÚLTIMA atividade do AVPlay (op síncrona OU evento nativo), persistido no
 * localStorage.
 *
 * <p><b>Op síncrona:</b> `open`/`prepare`/`stop`/`close`/`play`/`setDisplayRect` são
 * SÍNCRONAS e, no firmware SSSP, poderiam BLOQUEAR a main thread. Marcamos ANTES de cada
 * uma; se travar DENTRO, o crumb fica com a op que NÃO completou.
 *
 * <p><b>Evento nativo (2026-07-23):</b> o breadcrumb da v1.0.266 provou que NÃO era bloqueio
 * síncrono — os 2 crashes instrumentados mostraram `op=running` (o `play()` retornou, JS
 * saudável, heap plano) e ~8s depois o processo MORRE tocando "bakh" (H.264 Main trivial).
 * Ou seja: morte NATIVA durante o playback. Pra ver ONDE na reprodução, agora também
 * gravamos o último EVENTO nativo + o playtime: `playing`(throttle ~2s = "até onde chegou"),
 * `buffering-start`/`buffering-complete` (stall?), `stream-completed` (morreu no loop dos
 * 16s?), `avplay-error`(+string do erro nativo). No próximo freeze o crumb diz o instante e
 * o tipo de evento em que parou.
 *
 * <p>O ring buffer do console SOME no restart; isto sobrevive. SÓ localStorage + console —
 * NADA na tela (bancada exposta ao cliente). */
const AVPLAY_OP_KEY = 'efflux_avplay_last_op_v1';
function writeAvplayCrumb(payload: {
  op: string;
  detail?: string;
  playtimeMs?: number;
}): void {
  try {
    (window as unknown as { localStorage?: Storage }).localStorage?.setItem(
      AVPLAY_OP_KEY,
      JSON.stringify({ ...payload, ts: Date.now() }),
    );
  } catch {
    /* localStorage cheio/indisponível — ignora */
  }
}
function markAvplayOp(op: string, detail?: string): void {
  writeAvplayCrumb({ op, detail: detail ? detail.slice(-40) : undefined });
}
// Playtime é gravado por `oncurrentplaytime`, que dispara ~a cada 500ms-1s. Throttle a ~2s
// pra não martelar o localStorage — 2s de resolução basta pra saber "morreu mid-play (~8s)"
// vs "morreu no loop (16s)". Se o playtime PARAR de avançar, o último valor = onde travou.
let lastPlaytimeCrumbAt = 0;
function markAvplayPlaytime(ms: number): void {
  const now = Date.now();
  if (now - lastPlaytimeCrumbAt < 2000) return;
  lastPlaytimeCrumbAt = now;
  writeAvplayCrumb({ op: 'playing', playtimeMs: Math.round(ms) });
}

/**
 * Contadores CUMULATIVOS do ciclo de vida do AVPlay na SESSÃO (instrumentação 2026-07-25). O
 * crash residual do consumer (op=playing ~0,5-6,5s DENTRO do vídeo, heap plano, ~a cada 2h, em
 * QUALQUER conteúdo — content-specific refutado) aponta acumulação de recurso NATIVO ao longo da
 * sessão. Estes contadores medem o CHURN por slot (opens/prepares/plays/stops/closes) pra
 * correlacionar "morreu após N ciclos" e detectar players não-fechados (`opens - closes` crescente
 * = leak de plano/decoder). Persistidos no localStorage — MESMO bridge do crumb (crashCapture lê a
 * cada heartbeat, SEM import → mantém o crashCapture platform-agnostic). SÓ localStorage: NADA na
 * tela (bancada exposta ao cliente). O módulo recarrega a cada boot → contagem começa do zero.
 */
const AVPLAY_COUNTERS_KEY = 'efflux_avplay_counters_v1';
const avplayCounters = {
  opens: 0,
  prepares: 0,
  plays: 0,
  stops: 0,
  closes: 0,
  errors: 0,
};
function persistAvplayCounters(): void {
  try {
    (window as unknown as { localStorage?: Storage }).localStorage?.setItem(
      AVPLAY_COUNTERS_KEY,
      JSON.stringify(avplayCounters),
    );
  } catch {
    /* localStorage cheio/indisponível — ignora */
  }
}
function bumpAvplayCounter(k: keyof typeof avplayCounters): void {
  avplayCounters[k]++;
  persistAvplayCounters();
}
/** Snapshot dos contadores (HUD/testes). */
export function getAvplayCounters(): Readonly<typeof avplayCounters> {
  return { ...avplayCounters };
}
/**
 * BOOT: preserva a contagem da sessão ANTERIOR e só então zera a viva (F0, 2026-07-27).
 *
 * <p>BUG QUE ISTO CORRIGE (provado por sonda CDP na QB50B em 27/07): o
 * `efflux_avplay_counters_prev_v1` estava **todo zerado** (`{"opens":0,…}`) enquanto o
 * `efflux_avplay_blackbox_prev_v1` tinha a curva real da sessão. Motivo: quem copiava live→prev
 * era o `preserveCrashTimeSnapshots()` do crashCapture, que só roda **quando um crash é
 * INFERIDO** — e a linha abaixo zera a chave viva na carga deste módulo. Em boot limpo (ou
 * quando a ordem de carga favorecia o zero) o `_prev` nunca recebia a contagem verdadeira. Ou
 * seja: exatamente na hora de saber "com quantos opens a sessão morreu", o dado não estava lá.
 *
 * <p>Aqui a cópia acontece no ÚNICO ponto que conhece a ordem: imediatamente antes do reset,
 * dentro do mesmo módulo. Vale pra TODO boot — não depende de o crash ter sido inferido.
 * O `preserveCrashTimeSnapshots` segue existindo para as demais chaves; esta passa a estar
 * correta antes dele rodar (idempotente — copiar o mesmo valor 2× é inofensivo).
 */
try {
  const previous = (
    window as unknown as { localStorage?: Storage }
  ).localStorage?.getItem(AVPLAY_COUNTERS_KEY);
  if (previous) {
    (window as unknown as { localStorage?: Storage }).localStorage?.setItem(
      'efflux_avplay_counters_prev_v1',
      previous,
    );
  }
} catch {
  /* localStorage indisponível — segue e zera (telemetria é best-effort) */
}
// Zera no boot (o módulo recarrega a cada sessão) pra não misturar contagem de sessões antigas —
// o snapshot pré-crash usado no diagnóstico vem dos heartbeats da PRÓPRIA sessão, mas resetar a
// chave viva evita uma leitura stale nos primeiros ~3s do novo boot.
persistAvplayCounters();

/**
 * Latência do ciclo AVPlay — FINGERPRINT da degradação progressiva (Fase 0, 2026-07-26). As
 * chaves `efflux_avplay_timing_v1`/`_prev` já eram LIDAS (health:report + crashCapture, formatando
 * `avTiming=…`) mas NUNCA escritas — instrumentação declarada-porém-MORTA. Aqui a implementamos:
 *  • `frameMs` = `open` → 1º frame na tela (`oncurrentplaytime` t>0): quanto o slot demora pra
 *    APARECER. Se cresce ao longo da sessão = pipeline nativo degradando (o sintoma "slots
 *    esticando" reportado ~17h). É o sinal que decide degradação-vs-morte-seca.
 *  • `prepMs`  = `prepare`(start) → `afterPrepare` (async OU síncrono).
 * Guarda last + max + samples. MESMO bridge por localStorage (crashCapture lê sem import de
 * código de plataforma). SÓ localStorage + console — NADA na tela (bancada exposta ao cliente).
 */
const AVPLAY_TIMING_KEY = 'efflux_avplay_timing_v1';
const avplayTiming = {
  lastPrepMs: 0,
  maxPrepMs: 0,
  lastFrameMs: 0,
  maxFrameMs: 0,
  samples: 0,
};
function persistAvplayTiming(): void {
  try {
    (window as unknown as { localStorage?: Storage }).localStorage?.setItem(
      AVPLAY_TIMING_KEY,
      JSON.stringify(avplayTiming),
    );
  } catch {
    /* localStorage cheio/indisponível — ignora */
  }
}
function recordPrepMs(ms: number): void {
  if (!(ms >= 0) || ms > 120_000) return; // guarda contra relógio maluco / outlier absurdo
  avplayTiming.lastPrepMs = Math.round(ms);
  if (ms > avplayTiming.maxPrepMs) avplayTiming.maxPrepMs = Math.round(ms);
  persistAvplayTiming();
}
function recordFrameMs(ms: number): void {
  if (!(ms >= 0) || ms > 120_000) return;
  avplayTiming.lastFrameMs = Math.round(ms);
  if (ms > avplayTiming.maxFrameMs) avplayTiming.maxFrameMs = Math.round(ms);
  avplayTiming.samples++; // 1 sample = 1 slot que chegou a mostrar frame
  persistAvplayTiming();
}
/** Snapshot da latência (HUD/testes). */
export function getAvplayTiming(): Readonly<typeof avplayTiming> {
  return { ...avplayTiming };
}
// Zera no boot, alinhado ao reset dos contadores acima. O snapshot crash-time vem do `_prev`,
// preservado pelo crashCapture no boot ANTES deste reset (crashCapture instala cedo no App;
// este módulo carrega no mount do Stream — depois).
persistAvplayTiming();

/**
 * C4 — recycle por opens (mitigação do leak de CMA). Cada `open()` vaza memória nativa que
 * `close()` NÃO recupera → cliff determinístico (medido na Fase 0: ~2,75 MB/open, heap plano).
 * O recycle (Opção A) DESTRÓI e RECRIA os 2 `<object>` — o `Stream` remonta o `<AvplayVideo>`
 * (troca de `key`) → React remove+recria os `<object>` → o plano NATIVO é recriado → **(hipótese
 * A, a validar no experimento) reclama o CMA**. Diferente da tentativa que quebrou (que só
 * fechava os players mas mantinha os `<object>` → getPlayer re-ligava em plano corrompido).
 *
 * SEAMLESS: o `Stream` dispara o remount durante um slot de IMAGEM (o `<img>` DOM cobre a tela;
 * o plano recriado fica ATRÁS → zero preto). Fora de imagem, só força perto do cliff.
 *
 * EXPERIMENTO (Renato 2026-07-27): Opção A PRIMEIRO; B (reload) só se A não reclamar o CMA.
 * Thresholds BAIXOS de propósito (bancada) → vários recycles/hora → dá pra ver no black-box se a
 * RAM-livre RECUPERA após cada recycle (SAWTOOTH = A funciona; queda monotônica atravessando os
 * recycles = A não reclama → cai pro B). ⚠️ Valores de EXPERIMENTO — em prod subir perto do
 * cliff medido. Kill-switch: `AVPLAY_RECYCLE_ENABLED=false`.
 *
 * REPROVADO (Renato 2026-07-27): a Opção A (recycle) crashou ~720 opens (+14% pior que o
 * baseline) — NÃO reclama o CMA. Kill-switch OFF. O flag é checado no CALL SITE (Stream), não
 * dentro de `shouldRecycleAvplay` (que fica PURO/testável). Mantido no código (gated) pra
 * telemetria/experimento futuro; o caminho definitivo do leak é o E1 (pre-render).
 */
export const AVPLAY_RECYCLE_ENABLED = false; // kill-switch OFF (A reprovado: crashou ~720)
const RECYCLE_THRESHOLD_OPENS = 40; // recicla no próximo slot de IMAGEM após isto (seamless)
const RECYCLE_FORCE_OPENS = 70; // força em QUALQUER fronteira após isto (mesmo vídeo→vídeo)
const AVPLAY_RECYCLES_KEY = 'efflux_avplay_recycles_v1';
let avplayRecycles = 0;
let opensAtLastRecycle = 0;
function persistAvplayRecycles(): void {
  try {
    (window as unknown as { localStorage?: Storage }).localStorage?.setItem(
      AVPLAY_RECYCLES_KEY,
      String(avplayRecycles),
    );
  } catch {
    /* localStorage cheio/indisponível — ignora */
  }
}
/** opens acumulados desde o último recycle (ou boot). */
export function avplayOpensSinceRecycle(): number {
  return Math.max(0, avplayCounters.opens - opensAtLastRecycle);
}
/** Nº de recycles nesta sessão (HUD/telemetria). */
export function getAvplayRecycles(): number {
  return avplayRecycles;
}
/** Marca que um recycle vai acontecer AGORA (chamado pelo Stream ANTES de remontar). */
export function noteAvplayRecycle(): void {
  avplayRecycles++;
  opensAtLastRecycle = avplayCounters.opens;
  persistAvplayRecycles();
  markAvplayOp('recycle', `n=${avplayRecycles} opens=${avplayCounters.opens}`);
}
/**
 * Decisão PURA (testável) de recycle no boundary de slot. `isImageSlot` = próximo slot é imagem
 * (recycle SEAMLESS por trás do `<img>`). `>= FORCE` recicla em qualquer fronteira (pode piscar,
 * rede de segurança perto do cliff). `null` = não reciclar agora.
 *
 * PURA de propósito: o kill-switch `AVPLAY_RECYCLE_ENABLED` é checado no CALL SITE (Stream), não
 * aqui — assim os testes desta função não dependem do flag. Com o recycle OFF (reprovado), o
 * Stream nunca chega a chamar isto.
 */
export function shouldRecycleAvplay(
  opensSinceRecycle: number,
  isImageSlot: boolean,
): 'seamless' | 'force' | null {
  if (isImageSlot && opensSinceRecycle >= RECYCLE_THRESHOLD_OPENS)
    return 'seamless';
  if (opensSinceRecycle >= RECYCLE_FORCE_OPENS) return 'force';
  return null;
}
persistAvplayRecycles(); // zera no boot (alinhado aos contadores/latência)

/**
 * Listener NEUTRO (todos no-op). Aplicado no player que SAI de cena ANTES do `stop()`:
 * um player parado pode disparar `onstreamcompleted`/`onerror` em alguns firmwares Tizen —
 * se ele ainda tivesse o listener real, o Stream avançaria DE NOVO (pulava o próximo slot)
 * ou cairia pro `<video>` à toa. Silenciá-lo mata o avanço/fallback duplo na troca.
 */
const DEAD_LISTENER: Record<string, unknown> = {
  onbufferingstart: () => {},
  onbufferingprogress: () => {},
  onbufferingcomplete: () => {},
  oncurrentplaytime: () => {},
  onstreamcompleted: () => {},
  onerror: () => {},
  onerrormsg: () => {},
  onevent: () => {},
  onresourceconflicted: () => {}, // player aposentado NÃO dispara recovery
  ondrmevent: () => {},
};

/** Callbacks do ciclo do AVPlay — espelham os eventos do `<video>` usados pelo Stream. */
export interface AvplayListeners {
  /** prepare concluído (metadata + 1º frame prontos) ~= onLoadedData/onCanPlay. */
  onReady?: (durationMs: number) => void;
  /** currentTime avançou (frames tocando de verdade) ~= onPlaying (1×) + onTimeUpdate. */
  onTimeUpdate?: (currentMs: number) => void;
  /** stream terminou ~= onEnded. */
  onEnded?: () => void;
  /** erro irrecuperável ~= onError → chamador cai pro <video>. */
  onError?: (reason: string) => void;
  /**
   * A URL não é mídia — é PÁGINA (ex.: `youtube.com/watch?v=…`). Distinto de {@link onError} de
   * propósito, e a distinção é a razão de existir deste callback (bug de campo 2026-07-30):
   *
   * <p>`onError` significa "o AVPlay não conseguiu; tente o `<video>` do DOM". Para URL de página
   * isso é a PIOR resposta possível: o DOM também não toca (o Chromium nem toca HLS), então o
   * fallback virava `<video>` preso em `readyState=0` e **preto para sempre**. Pior: como o AVPlay
   * já tinha aberto e errado, o pipeline morria e a campanha INTEIRA parava — não só o item ruim.
   *
   * <p>Aqui NADA é aberto, parado ou fechado: a sessão do AVPlay segue viva segurando o frame
   * anterior. O chamador deve **PULAR o slot** e seguir o loop. É o "não sair do AVPlay".
   */
  onUnplayable?: (reason: string) => void;
  /** buffering começou ~= onWaiting. */
  onBufferingStart?: () => void;
  /** buffering terminou / voltou a tocar ~= onPlaying/canplay. */
  onBufferingComplete?: () => void;
}

/** Estado interno mínimo do AVPlay que expomos (subset do getState nativo). */
export type AvplayState =
  | 'NONE'
  | 'IDLE'
  | 'READY'
  | 'PLAYING'
  | 'PAUSED'
  | 'unknown';

interface RawAvplay {
  open: (url: string) => void;
  close: () => void;
  prepare: () => void;
  prepareAsync?: (ok: () => void, err: (e: unknown) => void) => void;
  play: () => void;
  stop: () => void;
  pause: () => void;
  seekTo?: (ms: number) => void;
  setListener: (l: Record<string, unknown>) => void;
  setDisplayRect: (x: number, y: number, w: number, h: number) => void;
  setDisplayMethod?: (m: string) => void;
  setDisplayRotation?: (r: string) => void;
  setVideoStillMode?: (v: string) => void;
  getState?: () => string;
  getDuration?: () => number;
  getCurrentTime?: () => number;
  getVersion?: () => string;
}

interface AvplayWindow {
  webapis?: {
    avplay?: RawAvplay;
    avplaystore?: { getPlayer: () => RawAvplay };
  };
}

/**
 * Adquire uma instância NOVA do AVPlay. Com `avplaystore` (SSSP), cada `getPlayer()` liga
 * no PRÓXIMO `<object type=application/avplayer>` livre (ordem do DOM) → precisa de 2 planos
 * no DOM pra alternar. Sem `avplaystore`, cai pro singleton `webapis.avplay` (1-player only —
 * o close-the-past degrada pro comportamento antigo, mas não quebra). `null` = indisponível.
 */
function acquireRawAvplay(): RawAvplay | null {
  const w = window as unknown as AvplayWindow;
  if (w.webapis?.avplaystore?.getPlayer) {
    return w.webapis.avplaystore.getPlayer(); // pode lançar QuotaExceeded — chamador trata
  }
  return w.webapis?.avplay ?? null;
}

/** `webapis.avplay(store)` presente? (não garante que TOCA — só que a API existe.) */
export function isAvplayAvailable(): boolean {
  try {
    const w = window as unknown as AvplayWindow;
    return !!(w.webapis?.avplaystore?.getPlayer || w.webapis?.avplay);
  } catch {
    return false;
  }
}

/**
 * Tizen 9+ (via UA). No 9.0 o `prepare()` SÍNCRONO retorna "pronto o suficiente" mas o decoder
 * ainda não assentou → `play()` imediato mata o nativo ~2-6s no playback (resíduo do consumer
 * UN50U8600). O `prepareAsync` espera o decoder confirmar antes do play → candidato a resolver.
 * SÓ no 9+: no SSSP 6.5 o `prepareAsync` em file:// QUEBRA (regressão 1.0.231) → lá fica síncrono.
 * Firmware 6.5 → false → mantém o caminho síncrono estável da frota.
 */
export function isTizen9Plus(): boolean {
  try {
    const m = /Tizen (\d+)\./.exec(navigator.userAgent || '');
    return m ? parseInt(m[1], 10) >= 9 : false;
  } catch {
    return false;
  }
}

/**
 * Controlador do slot de vídeo AVPlay — gerencia DOIS players internamente (close-the-past).
 * `this.cur` é o player NA TELA; a cada troca vídeo→vídeo um player NOVO entra e o `this.prev`
 * (passado) é FECHADO só depois do frame novo subir. Uma instância por slot de vídeo
 * fullscreen. `null` se o AVPlay não estiver disponível (chamador → fallback `<video>`).
 */
export class TizenAvplayController {
  private cur: RawAvplay | null; // player atual (na tela)
  private prev: RawAvplay | null = null; // player do PASSADO, aguardando close
  private listeners: AvplayListeners;
  private started = false;
  private disposed = false;
  private singleOnly: boolean; // sem avplaystore → 1 player só (degrada gracioso)
  private durMs = 0; // duração do vídeo atual (pro watchdog de fim)
  private endTimer: ReturnType<typeof setTimeout> | null = null; // watchdog de fim
  // telemetria leve (HUD/debug)
  got = 0;
  closed = 0;
  // C2 (onresourceconflicted + recoverInPlace): URL/geometria do vídeo atual (pro re-open) +
  // estado do cap de recovery (janela deslizante + debounce + contagem).
  private curUrl = '';
  private curRect: [number, number, number, number] = [0, 0, 1920, 1080];
  private curRot: 0 | 90 | 180 | 270 = 0;
  private recoverWindowStart = 0;
  private recoverCount = 0;
  private lastRecoverAt = 0;
  // Fase 0 (timing): instante do `open` + do `prepare` do slot atual — medem open→1º-frame e
  // prepare→ready (degradação progressiva). `firstFrameSeen` garante 1 medida de frame por open.
  private openAt = 0;
  private firstFrameSeen = false;
  private prepStartAt = 0;

  private constructor(
    first: RawAvplay,
    listeners: AvplayListeners,
    singleOnly: boolean,
  ) {
    this.cur = first;
    this.got = 1;
    this.listeners = listeners;
    this.singleOnly = singleOnly;
  }

  /** Cria o controlador (adquire o 1º player), ou `null` se o AVPlay não existe. */
  static create(listeners: AvplayListeners): TizenAvplayController | null {
    const w = window as unknown as AvplayWindow;
    const hasStore = !!w.webapis?.avplaystore?.getPlayer;
    let first: RawAvplay | null = null;
    try {
      first = acquireRawAvplay();
    } catch {
      first = null;
    }
    if (!first) return null;
    // EXPERIMENTO: FORCE_SINGLE_PLAYER força o reuso de 1 player mesmo com avplaystore (SSSP).
    return new TizenAvplayController(
      first,
      listeners,
      !hasStore || FORCE_SINGLE_PLAYER,
    );
  }

  version(): string {
    try {
      return this.cur?.getVersion?.() ?? '?';
    } catch {
      return '?';
    }
  }

  state(): AvplayState {
    try {
      return (this.cur?.getState?.() as AvplayState) ?? 'unknown';
    } catch {
      return 'unknown';
    }
  }

  currentTimeMs(): number {
    try {
      return Number(this.cur?.getCurrentTime?.() ?? 0);
    } catch {
      return 0;
    }
  }

  durationMs(): number {
    try {
      return Number(this.cur?.getDuration?.() ?? 0);
    } catch {
      return 0;
    }
  }

  /**
   * Listener nativo AMARRADO a UM player (`self`). Só age se `self` ainda é o `this.cur`
   * (guarda contra eventos de um player que já saiu de cena / está sendo fechado — evita
   * avanço/erro duplo). No 1º frame do player atual (currentTime>0), FECHA o `this.prev`
   * (o passado) — que está invisível ATRÁS → libera o decoder sem preto.
   */
  private nativeListenerFor(self: RawAvplay): Record<string, unknown> {
    return {
      onbufferingstart: () =>
        this.safe(() => {
          if (self === this.cur) {
            markAvplayOp('buffering-start'); // stall de buffer mid-play? (crumb imediato)
            this.listeners.onBufferingStart?.();
          }
        }),
      onbufferingprogress: () => {},
      onbufferingcomplete: () =>
        this.safe(() => {
          if (self === this.cur) {
            markAvplayOp('buffering-complete');
            this.listeners.onBufferingComplete?.();
          }
        }),
      oncurrentplaytime: (ms: unknown) => {
        const t = Number(ms) || 0;
        this.safe(() => {
          if (self !== this.cur) return;
          markAvplayPlaytime(t); // "até onde chegou" (throttle ~2s) — diagnóstico do freeze
          // Fase 0 (timing): 1º frame na tela fecha a medida open→frame (1× por open).
          if (t > 0 && !this.firstFrameSeen && this.openAt) {
            this.firstFrameSeen = true;
            recordFrameMs(Date.now() - this.openAt);
          }
          // frame novo NA TELA → agora sim fecha o PASSADO (invisível, atrás) → sem preto.
          if (t > 0 && this.prev) {
            log(`first frame (${t}ms) -> close past`);
            this.closePrev();
          }
          // WATCHDOG DE FIM: na Tizen o `onstreamcompleted` é NÃO-CONFIÁVEL (às vezes não
          // dispara no fim) → o vídeo congelaria no último frame sem avançar (o Stream já
          // limpou o watchdog dele no 1º frame). Quando chega perto do fim, arma um timer;
          // se o `onstreamcompleted` vier, cancela; senão, FORÇA o onEnded (padrão de
          // watchdog por currentTime >= duração-margem).
          if (
            this.durMs > 0 &&
            t >= this.durMs - END_NEAR_MS &&
            !this.endTimer
          ) {
            this.endTimer = setTimeout(() => {
              this.endTimer = null;
              log(`end watchdog fired (t~${t}/${this.durMs}) -> onEnded`);
              this.safe(() => {
                if (self === this.cur) this.listeners.onEnded?.();
              });
            }, END_GRACE_MS);
          }
          this.listeners.onTimeUpdate?.(t);
        });
      },
      onstreamcompleted: () =>
        this.safe(() => {
          this.clearEndTimer();
          if (self === this.cur) {
            markAvplayOp('stream-completed'); // fim natural → loop; morreu aqui = teardown/loop
            this.listeners.onEnded?.();
          }
        }),
      onerror: (e: unknown) =>
        this.safe(() => {
          // Marca SEMPRE (mesmo se não-cur): a string do erro nativo é ouro pro diagnóstico.
          markAvplayOp('avplay-error', String(e));
          bumpAvplayCounter('errors');
          if (self === this.cur)
            this.listeners.onError?.(`avplay:${String(e).slice(0, 60)}`);
        }),
      // C2 (Samsung): decoder reclamado pelo sistema / 2º processo — o ÚNICO callback documentado
      // que dispara ANTES do SO matar o playback. Sem ele, um conflito vira freeze/preto SEM
      // onerror nem fallback (perfil do hang bakh/FYS). Crumb + recovery in-place (re-open do vídeo
      // atual, re-adquire o decoder) com cap/debounce — NÃO cai pro <video> (AVPlay sempre ON).
      onresourceconflicted: () =>
        this.safe(() => {
          markAvplayOp('resource-conflicted', this.curUrl);
          bumpAvplayCounter('errors');
          if (self === this.cur) this.recoverInPlace();
        }),
      // M1: erro RICO (id + string legível) — melhor que o onerror cru (só o enum). Só crumb.
      onerrormsg: (id: unknown, msg: unknown) =>
        this.safe(() =>
          markAvplayOp('avplay-errmsg', `${String(id)}:${String(msg)}`),
        ),
      // M2: eventos nativos (PLAYER_MSG_HTTP_ERROR_CODE / resolution / bitrate) — crumb (útil no
      // path http remoto, ex.: R2 presigned que expira). Só crumb, sem mudar controle de fluxo.
      onevent: (id: unknown, data: unknown) =>
        this.safe(() =>
          markAvplayOp('avplay-event', `${String(id)}:${String(data)}`),
        ),
    };
  }

  /**
   * C2 — recovery in-place após `onresourceconflicted` (decoder reclamado): re-carrega o vídeo
   * ATUAL no MESMO player, re-adquirindo o decoder — SEM cair pro `<video>` (AVPlay sempre ON).
   * Reusa o `load()` tratando como carga inicial (`started=false` → re-open no `cur`). Cap +
   * debounce + janela deslizante evitam o loop open→conflita→reopen que aceleraria a morte de CMA.
   */
  private recoverInPlace(): void {
    if (this.disposed || !this.cur || !this.curUrl) {
      this.fail('resourceconflict:no-url');
      return;
    }
    const now = Date.now();
    // janela deslizante: reseta o cap a cada RESOURCE_CONFLICT_WINDOW_MS.
    if (now - this.recoverWindowStart > RESOURCE_CONFLICT_WINDOW_MS) {
      this.recoverWindowStart = now;
      this.recoverCount = 0;
    }
    if (now - this.lastRecoverAt < RESOURCE_CONFLICT_DEBOUNCE_MS) return; // debounce anti-loop
    if (this.recoverCount >= RESOURCE_CONFLICT_MAX_RECOVERIES) {
      // esgotou as tentativas na janela → segura no frame atual (não insiste no loop que acelera
      // a morte de CMA). Melhor congelado que num open→conflita→reopen infinito.
      markAvplayOp('resourceconflict-hold', `n=${this.recoverCount}`);
      log('resourceconflict: cap de recovery atingido — hold');
      return;
    }
    this.lastRecoverAt = now;
    this.recoverCount++;
    markAvplayOp(
      'recover-in-place',
      `st=${this.state()} n=${this.recoverCount}`,
    );
    this.trySet(() => this.cur?.stop()); // volta pra IDLE antes do re-open
    this.started = false; // load() trata como carga inicial → re-open no MESMO player
    this.load(this.curUrl, { rect: this.curRect, rotationDeg: this.curRot });
  }

  /**
   * Carrega e toca uma URL (cache local `file://`). Troca vídeo→vídeo = close-the-past:
   * congela o frame do player atual, PARA ele (libera o decoder — nunca 2 decodando juntos),
   * sobe um player NOVO, toca, e fecha o antigo quando o frame novo aparece. Qualquer erro
   * → onError (→ fallback pro `<video>`). `rect`/`rotationDeg` posicionam o plano HW.
   */
  load(
    url: string,
    opts: {
      still?: boolean;
      rect?: [number, number, number, number];
      rotationDeg?: 0 | 90 | 180 | 270;
    } = {},
  ): void {
    // FREEZE FORENSICS (2026-07-27): este `return` era SILENCIOSO. Num freeze de 6min medido na
    // bancada (duas reordenações de playlist em rajada) ficamos sem saber se o `load` tinha sido
    // CHAMADO-e-ignorado ou nem chamado — a diferença entre "controller morto sendo reusado" e
    // "efeito de load não re-disparou". Agora deixa rastro: se o crumb mostrar `load-skip:disposed`
    // no próximo freeze, a causa está provada.
    if (this.disposed) {
      markAvplayOp('load-skip:disposed', url);
      return;
    }
    // 🔴 GUARDA DE URL (bug medido em campo, 2026-07-30) — antes de QUALQUER coisa, e em especial
    // antes do `open()`. O servidor mandou `contentType:"video"` com `contentUrl` =
    // `youtube.com/watch?v=…`; o `open()` recebeu HTML, deu `errors:1 / plays:0`, e o pipeline
    // morreu levando a campanha inteira. Aqui a sessão atual NÃO é tocada — nem open, nem stop, nem
    // close — então o frame anterior continua no plano HW e o loop segue vivo. Vide `playableUrl`.
    const verdict = isPlayableMediaUrl(url);
    if (!verdict.playable) {
      markAvplayOp(
        `load-skip:unplayable`,
        `${verdict.reason} ${url.slice(0, 60)}`,
      );
      log(`load RECUSADO (${verdict.reason}) — slot deve ser pulado`);
      this.listeners.onUnplayable?.(verdict.reason ?? 'unplayable');
      return;
    }
    const rect = opts.rect ?? [0, 0, 1920, 1080];
    const rotationDeg = opts.rotationDeg ?? 0;
    const isSwitch = this.started && !!this.cur; // já tocava → troca
    const wantStill = isSwitch && !!opts.still && USE_STILLMODE;

    log(
      `load ${isSwitch ? 'switch' : 'init'} still=${wantStill} …${url.slice(-26)}`,
    );
    this.clearEndTimer(); // vídeo novo → zera o watchdog de fim do anterior
    try {
      let fresh: RawAvplay | null;
      if (isSwitch) {
        const old = this.cur!;
        // SILENCIA o player que sai ANTES de parar: um player parado pode disparar
        // onstreamcompleted/onerror → avanço/fallback duplo no Stream (pula slot). Vide
        // DEAD_LISTENER. Só depois disso congela/para.
        this.trySet(() => old.setListener(DEAD_LISTENER));
        markAvplayOp('stop-old', url);
        bumpAvplayCounter('stops');
        if (this.singleOnly) {
          // Sem avplaystore (só singleton): não dá pra ter 2 players. Degrada pro caminho
          // antigo (still→stop→reopen no MESMO player). Sem close (fecharia o plano visível).
          fresh = old;
          if (wantStill) this.trySet(() => old.setVideoStillMode?.('true'));
          this.trySet(() => old.stop());
        } else {
          // congela o frame do ATUAL e para ele (libera o decoder) — vira o "passado".
          if (wantStill) this.trySet(() => old.setVideoStillMode?.('true'));
          this.trySet(() => old.stop());
          // troca rápida: se ainda há um passado NÃO fechado, fecha antes de adquirir novo.
          if (this.prev && this.prev !== old) this.closePrev();
          this.prev = old; // fechado quando o frame do NOVO subir
          fresh = this.acquire();
          if (!fresh) {
            this.fail('acquire:null');
            return;
          }
          this.cur = fresh;
          this.got++;
        }
      } else {
        fresh = this.cur; // 1ª carga usa o player do create()
        if (!fresh) {
          this.fail('no-player');
          return;
        }
      }

      // C2: guarda a URL/geometria atuais pro re-open in-place no resource-conflict.
      this.curUrl = url;
      this.curRect = rect;
      this.curRot = rotationDeg;
      // Fase 0 (timing): marca o início do open; o 1º frame (oncurrentplaytime t>0) fecha a medida.
      this.openAt = Date.now();
      this.firstFrameSeen = false;
      markAvplayOp('open', url);
      bumpAvplayCounter('opens');
      // Fronteira B/C do orçamento do preto: até aqui é boot da app; daqui pra frente é decoder.
      // Idempotente, então só o 1º open do boot conta (vide bootTiming).
      markBoot('open');
      fresh.open(url);
      fresh.setListener(this.nativeListenerFor(fresh));
      fresh.setDisplayRect(rect[0], rect[1], rect[2], rect[3]);
      // player NOVO (2-player) → é a 1ª vez DELE, configura o método de display. No modo
      // singleOnly a troca NÃO chama (resetava o plano na 6.5 — comportamento antigo).
      if (!isSwitch || !this.singleOnly) {
        this.trySet(() =>
          fresh!.setDisplayMethod?.('PLAYER_DISPLAY_MODE_FULL_SCREEN'),
        );
      }
      this.applyRotationOn(fresh, rotationDeg);
      // PREPARE — assíncrono SÓ no Tizen 9+; na 6.5 volta ao `prepare()` SÍNCRONO (S1, 2026-07-27).
      // O still('true') do freeze/switch segura o frame durante o prepare (sync OU async) →
      // seamless mantido; afterPrepare solta o still e dá play.
      const runAfter = (): void => this.afterPrepare(fresh!, url, isSwitch);
      this.prepStartAt = Date.now(); // Fase 0 (timing): prepare→ready fecha no afterPrepare
      bumpAvplayCounter('prepares');
      // S1 (2026-07-27) — REVERTE o Fix A da 1.0.278 na Tizen 6.5, restaurando o gate
      // `isTizen9Plus()`. O Fix A expandiu o `prepareAsync` pra TODA Tizen apostando que o
      // bloqueio de main thread do síncrono causava o "slot esticando". A medição de 5 dias
      // (116h, 72.597 eventos do IndexedDB) mostrou o oposto:
      //  • morrer DENTRO do `prepareAsync` deixa o processo TRAVADO — o launcher do Tizen não
      //    detecta término e NUNCA relança: 6 das 10 mortes, com 15min a 10h de tela apagada;
      //  • as 4 mortes fora do async saíram limpas e o Tizen relançou em 26-29s;
      //  • disponibilidade medida: ~61% (45,5h apagada em 116h; 37,5h delas noturnas);
      //  • custo real do síncrono, medido aqui (`efflux_avplay_timing_v1`): 187ms típico,
      //    334ms no pior caso.
      // Trade-off: 334ms de thread bloqueada contra 45h de tela preta. A regressão já estava
      // documentada em `isTizen9Plus()` — o comentário dizia "na 6.5 o prepareAsync em file://
      // QUEBRA (regressão 1.0.231)" enquanto o código fazia o contrário.
      // O timeout/fallback abaixo NÃO protegia: a morte é um fault nativo instantâneo dentro da
      // chamada, então o watchdog de 5s nunca chega a rodar. Mesma classe do `releaseHold`
      // (revertido na 1.0.272) — a solução é a mesma: não chamar.
      // Segue valendo pro 9+, onde o async confirma o decoder antes do play (evita o play-cedo
      // que matava ~2-6s no UN50U8600) — lá o caminho é inalterado.
      // Desligar `S1_PREPARE_SYNC_65` no override restaura exatamente o comportamento da 1.0.299.
      const asyncAllowed = isChangeOn('S1_PREPARE_SYNC_65')
        ? isTizen9Plus()
        : true;
      if (!asyncAllowed) {
        noteChangeAction(
          'S1_PREPARE_SYNC_65',
          `sync opens=${avplayCounters.opens}`,
        );
      }
      if (asyncAllowed && typeof fresh.prepareAsync === 'function') {
        markAvplayOp('prepare-async', url);
        let settled = false;
        let asyncTimer: ReturnType<typeof setTimeout> | null = null;
        const syncFallback = (why: string): void => {
          if (settled || this.disposed || this.cur !== fresh) return;
          settled = true;
          if (asyncTimer) clearTimeout(asyncTimer);
          log(`prepareAsync → fallback síncrono (${why})`);
          try {
            markAvplayOp('prepare-sync-fb', url);
            fresh!.prepare(); // SÍNCRONO — o async travou/errou; segura a thread mas TOCA
            runAfter();
          } catch (e) {
            this.fail(`prepare-fb:${String(e).slice(0, 40)}`);
          }
        };
        asyncTimer = setTimeout(
          () => syncFallback('timeout'),
          PREPARE_ASYNC_TIMEOUT_MS,
        );
        fresh.prepareAsync(
          () => {
            if (settled) return;
            settled = true;
            if (asyncTimer) clearTimeout(asyncTimer);
            this.safe(runAfter);
          },
          (e) => syncFallback(`err:${String(e).slice(0, 30)}`),
        );
      } else {
        markAvplayOp('prepare', url);
        fresh.prepare(); // SÍNCRONO (sem prepareAsync disponível)
        runAfter();
      }
    } catch (e) {
      this.fail(`load:${String(e).slice(0, 50)}`);
    }
  }

  /**
   * Pós-prepare (comum ao prepare SÍNCRONO e ao prepareAsync do 9+): solta o still-mode, dá
   * play, marca running/started, arma o watchdog de close-past. No caminho async, `this.cur`
   * pode ter mudado durante o prepare (troca rápida de slot) → só toca se `fresh` ainda é o
   * player atual (evita tocar um player que já virou "passado").
   */
  private afterPrepare(fresh: RawAvplay, url: string, isSwitch: boolean): void {
    if (this.disposed || this.cur !== fresh) return;
    if (this.prepStartAt) recordPrepMs(Date.now() - this.prepStartAt); // Fase 0 (timing)
    // SEMPRE solta o still-mode ANTES do play (no-op se não havia still). Se o slot anterior foi
    // imagem, o freeze() deixou este player em still('true') → sem isto o vídeo tocava CONGELADO.
    this.trySet(() => fresh.setVideoStillMode?.('false'));
    markAvplayOp('play', url);
    bumpAvplayCounter('plays');
    fresh.play();
    markAvplayOp('running', url);
    this.started = true;
    this.durMs = this.durationMs(); // pro watchdog de fim (0 se o firmware não reportar)
    log(
      `playing (got=${this.got} closed=${this.closed} prevPend=${!!this.prev} dur=${this.durMs})`,
    );
    this.listeners.onReady?.(this.durMs);
    // watchdog: se o oncurrentplaytime atrasar, não segura 2 planos indefinidamente.
    if (isSwitch && !this.singleOnly && this.prev) {
      const pendingOld = this.prev;
      setTimeout(() => {
        if (this.prev === pendingOld) this.closePrev();
      }, CLOSE_PAST_FALLBACK_MS);
    }
  }

  /** Adquire player novo; em quota (2 players + o pendente), fecha o passado e tenta 1×. */
  private acquire(): RawAvplay | null {
    try {
      return acquireRawAvplay();
    } catch {
      this.closePrev(); // libera um slot
      try {
        return acquireRawAvplay();
      } catch {
        return null;
      }
    }
  }

  /** Cancela o watchdog de fim de vídeo. Idempotente. */
  private clearEndTimer(): void {
    if (this.endTimer) {
      clearTimeout(this.endTimer);
      this.endTimer = null;
    }
  }

  /** Fecha o player do PASSADO (libera decoder + slot). Idempotente. */
  private closePrev(): void {
    const p = this.prev;
    if (!p) return;
    this.prev = null;
    markAvplayOp('close-past');
    this.trySet(() => p.stop());
    try {
      p.close();
      this.closed++;
      bumpAvplayCounter('closes');
      markAvplayOp('running'); // passado fechado com sucesso → sem chamada pendente
    } catch {
      /* noop */
    }
  }

  /**
   * Congela o último frame do player atual (`setVideoStillMode('true')`) + pausa. Usado no
   * video→imagem: mantém o frame do vídeo que saiu VISÍVEL enquanto a imagem DOM pinta POR
   * CIMA → sem teardown, sem preto. O próximo `load(still:true)` faz do atual o "passado".
   */
  freeze(): void {
    if (!this.cur) return;
    // NADA pra congelar antes do 1º vídeo tocar (started=false): o slot de boot é imagem
    // (mostrada via DOM), o plano HW está ocioso. Congelar aqui só POLUI o player (deixa em
    // still-mode) e o 1º vídeo, que REUSA esse player na carga inicial, tocava CONGELADO.
    // Só congela quando há de fato um frame de vídeo pra segurar (video→imagem, started=true).
    if (!this.started) return;
    this.trySet(() => this.cur!.setVideoStillMode?.('true'));
    this.trySet(() => this.cur!.pause());
  }

  /** Pausa (segura o frame atual no plano HW). */
  pause(): void {
    markAvplayOp('pause');
    this.trySet(() => this.cur?.pause());
  }

  /** Salta pra um instante (ms) — usado por recovery, não pelo fluxo normal. */
  seek(ms: number): void {
    markAvplayOp('seek', String(Math.round(ms)));
    this.trySet(() => this.cur?.seekTo?.(Math.max(0, Math.round(ms))));
  }

  setRect(
    x: number,
    y: number,
    w: number,
    h: number,
    rotationDeg: 0 | 90 | 180 | 270 = 0,
  ): void {
    if (!this.cur) return;
    // setDisplayRect roda DURANTE o playback (resize/rotate do viewport) → se bloquear a
    // main thread com o decoder ativo, o crumb aponta aqui em vez de 'running'.
    markAvplayOp('set-rect', `${w}x${h}@${rotationDeg}`);
    this.trySet(() => this.cur!.setDisplayRect(x, y, w, h));
    this.applyRotationOn(this.cur, rotationDeg);
    markAvplayOp('running'); // setRect retornou — volta pro estado tocando
  }

  /**
   * Rotaciona o VÍDEO no plano HW (portrait em painel cujo plano é nativo landscape). O
   * plano do AVPlay NÃO segue a auto-rotation do WebView → sem isto o vídeo sai deitado.
   * `setDisplayRotation` é opcional no firmware; ausência = no-op. 0 = não chama.
   */
  private applyRotationOn(av: RawAvplay, deg: 0 | 90 | 180 | 270): void {
    if (!deg) return;
    this.trySet(() =>
      av.setDisplayRotation?.(`PLAYER_DISPLAY_ROTATION_${deg}`),
    );
  }

  /** Para + fecha AMBOS os players (libera o decoder HW). Idempotente. */
  destroy(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.clearEndTimer();
    this.closePrev();
    const c = this.cur;
    this.cur = null;
    if (c) {
      markAvplayOp('destroy');
      // SILENCIA antes de parar/fechar — mesma proteção que o `switchTo` já aplicava e que
      // faltava aqui (2026-07-27). Um player parado/fechado pode disparar `onstreamcompleted`
      // ou `onerror` ATRASADO, e isso tem duas consequências ruins, ambas observadas:
      //  • o Stream trata como fim de vídeo → AVANÇA slot indevidamente (pula conteúdo do
      //    cliente, o mesmo motivo pelo qual o DEAD_LISTENER existe no switch);
      //  • o evento fantasma SOBRESCREVE o breadcrumb: no freeze de 6min medido na bancada o
      //    crumb mostrava `stream-completed` quando o último ato real tinha sido este destroy,
      //    o que atrasou o diagnóstico.
      this.trySet(() => c.setListener(DEAD_LISTENER));
      this.trySet(() => c.stop());
      try {
        c.close();
        this.closed++;
        bumpAvplayCounter('closes'); // conta o close do `cur` no destroy (antes só o closePrev
        // contava → o contador subcontava `closes` a cada recriação do controller, inflando o
        // aparente "opens - closes"; o player SEMPRE foi fechado aqui, só não era contado).
        markAvplayOp('idle'); // destroy completo
      } catch {
        /* noop */
      }
    }
  }

  private fail(reason: string): void {
    log(`FAIL ${reason}`);
    this.safe(() => this.listeners.onError?.(`avplay:${reason}`));
  }

  /** Executa uma chamada nativa best-effort (não pode derrubar o fluxo). */
  private trySet(fn: () => void): void {
    try {
      fn();
    } catch {
      /* chamada nativa opcional/firmware-dependente — degrada gracioso */
    }
  }

  private safe(fn: () => void): void {
    try {
      fn();
    } catch {
      /* callback do chamador não pode derrubar o listener nativo */
    }
  }
}
